import React, { useState, useCallback, useEffect } from 'react';

import {
  Button,
  Box,
  Divider,
  TextField,
  Grid,
  Typography,
  Card,
  styled,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  OutlinedInput,
  Chip,
  ListItemText,
  Switch,
} from '@mui/material';

import dayjs from 'dayjs';

import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useUsers, userTypes, targetTypes } from '../../../hooks/api/useUsers';
// import { useCustomFields } from '../../../hooks/api/useCustomFields';

import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { mergeSchema, checkValueExists } from '../../../utils/dataSchema';
import { spliceString } from '../../../utils/formatString';
import { useRegions, regionTypes } from '../../../hooks/api/useRegions';

import {
  initialValues,
  initialPasswordValues,
  validationSchema,
  validationPasswordSchema,
} from '../../../schema/users/memberSchema';
// import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import DatePickerComponent from '../../common/DatePickerComponent';
import CommonRestricted from '../../common/CommonRestricted';

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useAppContextFeatures } from '../../../context/AppContext';
// import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function UpdateMember({ routeType, ownerType }) {
  const { id } = useParams();

  const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();
  const { promptMessage } = useSystemPrompt();
  // const { isLoading, get, update } = useRetailMembers();
  const { isLoading, get, update, filterOperatorTypes } = useUsers({
    userType: userTypes.member,
    targetType: ownerType,
  });
  const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.member.id);

  // const {
  //     dataCustomFields,

  //     getInitialFormStateRows,
  //     getSubmitFormDataFromRows,

  //     getCustomFieldOptionNameByIds,

  //     RenderCustomFieldSelectionTextbox,
  //     RenderCustomFieldSelectionDropdown,
  //     RenderCustomFieldSelectionMultiDropdown
  // } = useStateCustomFields(
  //     {
  //         fieldModule: customFieldModules.user,
  //         moduleFunction: customFieldFunctions.user.member
  //     }
  // )

  const { isLoadingRegion, list: listRegions } = useRegions({});

  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);

  const navigate = useNavigate();
  const [savedValues, setSavedValues] = useState(null);
  const { getRoute } = useSystemRoutes(routeType);
  const { getHtmlHead } = useSystemHead(routeType);
  // const [customFields, setCustomFields] = useState([])

  const [checked, setChecked] = useState(false);

  const getSchema = () => {
    if (checked === true) {
      return mergeSchema(validationSchema, validationPasswordSchema);
    }
    return validationSchema;
  };

  const handleAllowChangePassword = (event) => {
    setChecked(event.target.checked);
  };

  const loadData = useCallback(
    async (id) => {
      setSystemLoading(true);

      try {
        const { data } = await get({ id });
        const { member: retailMember, custom_fields: savedCustomFields = [] } = data;

        const cf = [];
        // await getInitialFormStateRows({
        //     savedCustomFields,
        // })

        setSavedValues({
          username: retailMember.username,
          firstname: retailMember.firstname,
          lastname: retailMember.lastname,
          contactno: retailMember.contact,
          identityno: retailMember.identity_no,
          dob: retailMember.dob ? dayjs(retailMember.dob) : dayjs(),
          gender: retailMember.gender,
          email: retailMember.email,
          status: Boolean(retailMember?.status),
          companyname: retailMember.company_name,
          regno: retailMember.reg_no,
          accountcode: retailMember.account_code ? retailMember.account_code : '',
          address: retailMember?.address_1,
          address2: retailMember?.address_2,
          postcode: retailMember?.postcode,
          countryId: retailMember?.country_id,
          stateId: retailMember?.state_id,
          areaId: retailMember?.area_id,
          remark: retailMember.remark,
          ...initialPasswordValues,
          rows: cf,
        });
      } catch (error) {
        promptMessage(false, `${error?.message}`);
      }

      setSystemLoading(false);
    },
    [id]
  );

  useEffect(() => {
    loadData(id);
    fetchDataCountry();
  }, []);

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      // const { data } = await listCountries(props)
      const { data } = await listRegions(props);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const onSubmit = async (currentValues) => {
    setSystemLoading(true);

    try {
      const { rows } = values;

      let cf = [];
      // getSubmitFormDataFromRows({ rows })

      let data = {
        credential: {
          firstname: currentValues?.firstname,
          lastname: currentValues?.lastname,
          password: currentValues?.password,
          contact: currentValues?.contactno,
          email: currentValues?.email,
          status: currentValues?.status ? 1 : 0,
        },
        member: {
          identity_no: currentValues?.identityno,
          gender: currentValues?.gender,
          dob: currentValues.dob.format('YYYY-MM-DD'),
          company_name: currentValues?.companyname,
          address_1: currentValues?.address,
          address_2: currentValues?.address2,
          country_id: currentValues?.countryId,
          state_id: currentValues?.stateId,
          area_id: currentValues?.areaId,
          postcode: currentValues?.postcode,
          remark: currentValues?.remark,
        },
        custom_fields: cf,
      };

      // console.log('data', data);

      const response = await update({ id, data, scopeType: userTypes.member, targetType: targetTypes.retail });

      promptMessage(true, `${response?.message}`);

      navigate(getRoute('users/members'));
    } catch (error) {
      promptMessage(false, `Action Failed. (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const formik = useFormik({
    initialValues: savedValues,
    validationSchema: getSchema(),
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    // console.log('postcodeArray', postcodeArray)

    // let a = []

    // postcodeArray.forEach(i => a.push(i.toString()))

    // console.log('a', a)

    // return a

    return postcodeArray;
  };

  const RenderChangePassword = () => {
    if (checked === false) return <></>;

    return (
      <>
        <Grid item xs={12} sm={12} md={6} textAlign={'left'}>
          <TextField
            margin="dense"
            size="small"
            fullWidth
            name={'password'}
            label="Password"
            type="password"
            variant="outlined"
            value={values.password}
            onChange={handleChange}
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} textAlign={'left'}>
          <TextField
            margin="dense"
            size="small"
            fullWidth
            name={'passwordconfirm'}
            label="Confirm Password"
            type="password"
            variant="outlined"
            value={values.passwordconfirm}
            onChange={handleChange}
            error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
            helperText={touched.passwordconfirm && errors.passwordconfirm}
          />
        </Grid>
      </>
    );
  };

  // const RenderCustomFieldSelection = () => {

  //     if (!values?.rows) return <></>

  //     const rows = values.rows

  //     return (
  //         <>
  //             <Grid item xs={12}>
  //                 <Divider sx={{ mt: 2, mb: 1 }} />
  //             </Grid>

  //             <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
  //                 <CommonSectionHeader
  //                     title='Custom Properties'
  //                     subTitle={"Provide custom item information"}
  //                 />
  //             </Grid>
  //             <Grid item xs={12} sm={12} md={9}>
  //                 <List>
  //                     {
  //                         values.rows.map((item, index) => {

  //                             const { rows } = values

  //                             let itemValues = rows.length ? rows[index] : {}

  //                             let {
  //                                 fieldType,
  //                                 fieldLabel,
  //                                 fieldKey
  //                             } = item

  //                             return (
  //                                 <ListItem
  //                                     key={index}
  //                                     divider
  //                                     disablePadding
  //                                     sx={{
  //                                         py: 1
  //                                     }}
  //                                 >
  //                                     <Grid container spacing={1}>
  //                                         <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
  //                                             <Chip
  //                                                 label={fieldLabel}
  //                                                 color='primary'
  //                                                 variant='outlined'
  //                                                 size='large'
  //                                                 sx={{
  //                                                     width: '100%',
  //                                                     // textAlign: 'left',
  //                                                     fontSize: 16,
  //                                                     fontWeight: 800
  //                                                 }}
  //                                             />
  //                                         </Grid>
  //                                         <Grid item md={9} xs={12} sm={12}>

  //                                             {
  //                                                 fieldType === inputTypesDef.textbox &&

  //                                                 RenderCustomFieldSelectionTextbox({
  //                                                     index,
  //                                                     item,
  //                                                     itemValues,
  //                                                     inputProps: {
  //                                                         fullWidth: true,
  //                                                         name: `rows.${index}.customValue`,
  //                                                         label: itemValues?.fieldLabel,
  //                                                         type: "text",
  //                                                         variant: "outlined",
  //                                                         value: itemValues?.customValue,
  //                                                         onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
  //                                                         // error: Boolean(itemErrors?.customValue),
  //                                                         // helperText: itemErrors?.customValue
  //                                                     }
  //                                                 })
  //                                             }

  //                                             {
  //                                                 fieldType === inputTypesDef.dropdown &&

  //                                                 RenderCustomFieldSelectionDropdown({
  //                                                     index,
  //                                                     item,
  //                                                     itemValues,
  //                                                     selectProps: {
  //                                                         labelId: "customValue",
  //                                                         label: itemValues?.fieldLabel,
  //                                                         name: `rows.${index}.customArray`,
  //                                                         onChange: (event) => {
  //                                                             setFieldValue(`rows.${index}.customArray`, [event.target.value])
  //                                                         },
  //                                                         size: 'small',
  //                                                         margin: 'dense',
  //                                                         value: itemValues.customArray.length ? itemValues.customArray[0] : ""
  //                                                     }
  //                                                 })
  //                                             }

  //                                             {
  //                                                 fieldType === inputTypesDef.multidropdown &&

  //                                                 RenderCustomFieldSelectionMultiDropdown({
  //                                                     index,
  //                                                     item,
  //                                                     itemValues,
  //                                                     selectProps: {
  //                                                         labelId: itemValues?.fieldLabel,
  //                                                         label: itemValues?.fieldLabel,
  //                                                         name: `rows.${index}.customArray`,
  //                                                         onChange: handleChange,
  //                                                         renderValue: (selected) => {

  //                                                             return (
  //                                                                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
  //                                                                     {
  //                                                                         getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
  //                                                                             <Chip
  //                                                                                 key={index}
  //                                                                                 // color={'red'}
  //                                                                                 label={spliceString({ val: i, maxLength: 20 })}
  //                                                                             />
  //                                                                         ))
  //                                                                     }
  //                                                                 </Box>
  //                                                             )

  //                                                         },
  //                                                         size: 'small',
  //                                                         margin: 'dense',
  //                                                         multiple: true,
  //                                                         input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
  //                                                         value: itemValues?.customArray ? itemValues.customArray : []
  //                                                     }
  //                                                 })
  //                                             }

  //                                         </Grid>
  //                                     </Grid>
  //                                 </ListItem>
  //                             )

  //                         })
  //                     }

  //                 </List>

  //             </Grid>
  //         </>
  //     )
  // }

  const RenderCredentialInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={'Credential'} subTitle={'Access Authentication'} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                disabled
                margin="dense"
                size="small"
                fullWidth
                name={'username'}
                label="Username"
                type="text"
                variant="outlined"
                value={values?.username}
                onChange={handleChange}
                // error={touched.username && Boolean(errors.username)}
                // helperText={touched.username && errors.username}
              />
            </Grid>

            <Grid item xs={12} md={6} textAlign="left">
              <FormControlLabel
                control={
                  <Switch
                    sx={{
                      ml: 2,
                      my: 1,
                    }}
                    name="status"
                    checked={Boolean(values.status)}
                    onChange={(event, checked) => {
                      setFieldValue('status', Boolean(checked));
                    }}
                  />
                }
                label={Boolean(values.status) ? 'Enabled' : 'Disabled'}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} textAlign="left">
              <Divider>
                <FormControlLabel
                  control={<Checkbox size="small" onChange={handleAllowChangePassword} />}
                  label="Update Password"
                />
              </Divider>
            </Grid>

            {RenderChangePassword()}
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderProfileInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={'Profile'} subTitle={'Details of the user information'} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={0.5}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'firstname'}
                label="First Name"
                type="text"
                variant="outlined"
                value={values.firstname}
                onChange={handleChange}
                error={touched.firstname && Boolean(errors.firstname)}
                helperText={touched.firstname && errors.firstname}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'lastname'}
                label="Last Name"
                type="text"
                variant="outlined"
                value={values.lastname}
                onChange={handleChange}
                error={touched.lastname && Boolean(errors.lastname)}
                helperText={touched.lastname && errors.lastname}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'email'}
                label="Email"
                type="email"
                variant="outlined"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'contactno'}
                label="Contact No."
                type="text"
                variant="outlined"
                value={values.contactno}
                onChange={handleChange}
                error={touched.contactno && Boolean(errors.contactno)}
                helperText={touched.contactno && errors.contactno}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'identityno'}
                label="Identity No."
                type="text"
                variant="outlined"
                value={values.identityno}
                onChange={handleChange}
                error={touched.identityno && Boolean(errors.identityno)}
                helperText={touched.identityno && errors.identityno}
              />
            </Grid>

            <Grid item xs={3}>
              <DatePickerComponent
                config={{
                  name: 'dob',
                  label: 'DOB',
                }}
                formik={{
                  values,
                  errors,
                  touched,
                  handleChange,
                  setFieldValue,
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.gender && Boolean(errors.gender)}>
                <InputLabel id="gender">Gender</InputLabel>
                <Select
                  labelId="gender"
                  label="Gender"
                  name={'gender'}
                  onChange={(event) => {
                    setFieldValue(`gender`, event.target.value);
                  }}
                  value={values.gender}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  <MenuItem value={'male'}>Male</MenuItem>
                  <MenuItem value={'female'}>Female</MenuItem>
                  <MenuItem value={'other'}>Other</MenuItem>
                </Select>
                <FormHelperText>{touched.gender && errors.gender}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'companyname'}
                label="Company Name"
                type="text"
                variant="outlined"
                value={values.companyname}
                onChange={handleChange}
                error={touched.companyname && Boolean(errors.companyname)}
                helperText={touched.companyname && errors.companyname}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'remark'}
                label="Remark"
                type="text"
                multiline
                rows={3}
                variant="outlined"
                value={values.remark}
                onChange={handleChange}
                error={touched.remark && Boolean(errors.remark)}
                helperText={touched.remark && errors.remark}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderAddressInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={'Address'} subTitle={'Full address details of supplier information'} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={0.5}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address'}
                label="Address 1"
                type="text"
                variant="outlined"
                value={values.address}
                onChange={handleChange}
                error={touched.address && Boolean(errors.address)}
                helperText={touched.address && errors.address}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address2'}
                label="Address 2"
                type="text"
                variant="outlined"
                value={values.address2}
                onChange={handleChange}
                error={touched.address2 && Boolean(errors.address2)}
                helperText={touched.address2 && errors.address2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.countryId && Boolean(errors.countryId)}>
                <InputLabel id="country">Country</InputLabel>
                <Select
                  labelId="countryId"
                  label="Country"
                  name={'countryId'}
                  onChange={(event) => {
                    setFieldValue(`countryId`, event.target.value);
                    setFieldValue(`stateId`, '');
                    setFieldValue(`areaId`, '');
                    setFieldValue(`postcode`, '');
                    setDataStates([]);
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataCountries,
                    valueToCompare: values.countryId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataCountries.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.country_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.stateId && Boolean(errors.stateId)}>
                <InputLabel id="state">State</InputLabel>
                <Select
                  labelId="stateId"
                  label="State"
                  name={'stateId'}
                  onChange={(event) => {
                    setFieldValue(`stateId`, event.target.value);
                    setFieldValue(`areaId`, '');
                    setFieldValue(`postcode`, '');
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataStates,
                    valueToCompare: values.stateId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataStates.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.state_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.areaId && Boolean(errors.areaId)}>
                <InputLabel id="area">Area</InputLabel>
                <Select
                  labelId="areaId"
                  label="area"
                  name={'areaId'}
                  onChange={(event) => {
                    setFieldValue(`areaId`, event.target.value);
                    setFieldValue(`postcode`, '');
                  }}
                  value={checkValueExists({
                    dataSearch: dataAreas,
                    valueToCompare: values.areaId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataAreas.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.area_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.postcode && Boolean(errors.postcode)}>
                <InputLabel id="postcode">Postcode</InputLabel>
                <Select
                  labelId="postcode"
                  label="Postcode"
                  name={'postcode'}
                  onChange={handleChange}
                  value={checkPostcodeExists()}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {getPostcodesArray().length ? (
                    getPostcodesArray().map((i, index) => {
                      return (
                        <MenuItem key={index} value={i}>
                          {i}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value={''}>{null}</MenuItem>
                  )}
                </Select>
                <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderForm = () => {
    if (!values) return <></>;

    return (
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={1}>
            {RenderCredentialInfo()}

            <Grid item xs={12} md={12} sm={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {RenderProfileInfo()}

            <Grid item xs={12} md={12} sm={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {RenderAddressInfo()}

            {/* {
                            RenderCustomFieldSelection()
                        } */}
          </Grid>
        </StyledCard>
        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <Button
              type="submit"
              // fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
            >
              Save Changes
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.updateMember })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle="Update Member"
        breadcrumbs={[
          {
            displayText: 'Members',
            props: {
              color: 'secondary.main',
              href: getRoute('users/members'),
            },
          },
          {
            displayText: 'Update Member',
            props: {
              disabled: true,
              color: 'secondary.main',
            },
          },
        ]}
      />

      {isPermissionAllow({ functionType: functionTypes.pageNew.id }) ? RenderForm() : <CommonRestricted />}
    </Box>
  );
}
